import React from 'react';

import { Crehanaicons175 } from '@crehana/icons';

import { TextField, Dropdown, Radio, PrimaryButton } from '@crehana/ui';

import { useTranslation } from '@crehana/i18n';

import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';

import InputGuestUser from 'Jsx/CheckoutV4/shared/InputGuestUser';
import PaymentsLoader from 'Jsx/CheckoutV4/shared/PaymentsLoader';
import SecurePayment from 'Jsx/CheckoutV4/shared/SecurePayment';
import LegalCheckbox from 'Jsx/CheckoutV4/components/LegalCheckbox';
import NewsLetterCheckbox from 'Jsx/CheckoutV4/components/NewsLetterCheckbox';

import { DocumentTypeEnum } from 'Jsx/Types/Graphql/globals.payments';

import CardIssuerLogo from 'Jsx/Shared/CardIssuerLogo';

import QuotasSelector from 'Jsx/CheckoutV4/components/QuotasSelector';

import { CountryPrefixEnum } from 'Jsx/CheckoutV4/types';

import getCountryPrefix from 'Jsx/Utils/getCountryPrefix';

import CardLogos from 'Jsx/CheckoutV4/components/CardLogos';

import { FormProps } from '../types';

import AnimatedCard from './AnimatedCard';

const Form: React.FunctionComponent<FormProps> = ({
  creditCardDigitsWithBlankSpaces,
  creditCardNumberErrorMessage,
  creditCardNumber,
  isCreditCardNumberValid,
  isProcessing,
  creditCardIssuer,
  creditCardExpiry,
  creditCardExpiryErrorMessage,
  isCreditCardExpiryValid,
  creditCardCVV,
  isCreditCardCVVValid,
  creditCardCVVDigits,
  creditCardCVVErrorMessage,
  isPaymentButtonActive,
  paymentError,
  onBlurCreditCardNumber,
  onChangeCreditCardNumber,
  onChageCreditCardExpiry,
  // @ts-ignore
  onChangeDocumentType,
  onBlurCreditCardExpiry,
  onChangeCreditCardCVV,
  onBlurCVV,
  onClickPay,
  onEmailChange,
  guestEmail,
  onChangeFullName,
  onBlurFullName,
  fullName,
  // @ts-ignore
  documentType,
  fullNameErrorMessage,
  isFullNameValid,
  onChangePhoneNumber,
  onBlurPhoneNumber,
  phoneNumber,
  phoneNumberErrorMessage,
  isPhoneNumberValid,
  identityDocument,
  identityDocumentErrorMessage,
  isIdentityDocumentValid,
  onChangeIdentityDocument,
  onBlurIdentityDocument,
  userAddress,
  userAddressErrorMessage,
  isUserAddressValid,
  onChangeUserAddress,
  onBlurUserAddress,
  cityName,
  cityNameErrorMessage,
  isCityNameValid,
  onChangeCityName,
  onBlurCityName,
  provinceOrStateName,
  onChangeProvinceOrStateName,
  onBlurProvinceOrStateName,
  provinceOrStateNameErrorMessage,
  isProvinceOrStateNameValid,
  userZipCode,
  userZipCodeErrorMessage,
  isUserZipCodeValid,
  onChangeUserZipCode,
  onBlurUserZipCode,
  documentMaxLength,
  zipMaxLength,
  phoneMaxLength,
  countryCod,
  expiryCardMaxLength,
  zipHint,
  areFirstStepsFieldsValid,
  documentHint,
  creditCardNumberFocus,
  creditCardExpiryFocus,
  creditCardCVVFocus,
  isMembership,
  checkedTerms,
  setCheckedTerms,
  checkedNewsLetterTerms,
  setCheckedNewsLetterTerms,
  zipDescription,
  DocDescription,
  brazilState,
  isEbanxCard,
  isEbanxCardArg,
  // @ts-ignore
  symbol,
  // @ts-ignore
  quotas,
  // @ts-ignore
  selectedQuotaNumber,
  // @ts-ignore
  setSelectedQuotaNumber,
  isDesktop,
  togglePaymentWithSavedCard,
  hasSavedCard,
}) => {
  const { setStepsToBuy, isGuestCheckout, showPaymenButtonRight } =
    useCheckoutContext();

  const { t } = useTranslation();

  const [hideCardSection, setHideCardSection] = React.useState(isEbanxCard);
  const countryPrefix = getCountryPrefix();
  const isColombianCheckout = countryPrefix === CountryPrefixEnum.COLOMBIA;
  const showPyamentButton =
    !showPaymenButtonRight || !isDesktop || hasSavedCard;

  React.useEffect(() => {
    setHideCardSection(isEbanxCard);
  }, [isEbanxCard]);

  React.useEffect(() => {
    if (!hideCardSection && isEbanxCard) {
      // @ts-ignore
      setStepsToBuy(
        // @ts-ignore
        [
          {
            label: t('PERSONAL_INFO'),
            status: 'completed',
          },
          {
            label: t('CARD_INFO'),
            status: 'current',
          },
          isGuestCheckout
            ? {
                label: t('ACTIVATION'),
                status: 'disabled',
              }
            : null,
          !isGuestCheckout
            ? {
                label: t('LAST_STEP'),
                status: 'disabled',
              }
            : null,
        ].filter(el => el !== null),
      );
    }
  }, [hideCardSection]);

  return (
    <div className="relative w-full m-auto">
      <p className="font-h2 mb-24 md:hidden">{t('PAYMENT_FORM_TITLE')}</p>
      <p className="font-h3 mb-24 hidden md:block">{t('PAYMENT_FORM_TITLE')}</p>
      {hasSavedCard && (
        <button
          type="button"
          onClick={togglePaymentWithSavedCard}
          className="mb-16 text-secondary-main font-link"
        >
          <Crehanaicons175
            size={12}
            ignoreColor
            className="fill-current text-secondary-main mr-8"
          />
          {t('GO_SAVED_CARD')}
        </button>
      )}
      <form
        onSubmit={event => {
          event.preventDefault();
          if (areFirstStepsFieldsValid && hideCardSection) {
            setHideCardSection(false);
          } else if (isPaymentButtonActive) {
            onClickPay();
          }
        }}
        data-testid="online-payment-form"
        style={{ maxWidth: 506 }}
      >
        {/* Additional card information fields */}
        {hideCardSection && (
          <div>
            {isEbanxCard && (
              <React.Fragment>
                <div className="w-full mb-24">
                  <TextField
                    type="tel"
                    maxLength={documentMaxLength}
                    id="identityDocument"
                    name="identityDocument"
                    data-testid="doc-field"
                    full
                    onChange={onChangeIdentityDocument}
                    onBlur={onBlurIdentityDocument}
                    value={identityDocument}
                    errorText={identityDocumentErrorMessage}
                    error={identityDocumentErrorMessage !== ''}
                    check={isIdentityDocumentValid}
                    disabled={isProcessing}
                    description={documentHint || DocDescription}
                    label={t('IDENTITY_DOCUMENT_LABEL')}
                  />
                </div>
                <div className="w-full mb-24">
                  <TextField
                    type="text"
                    maxLength={zipMaxLength}
                    id="userZipCode"
                    name="userZipCode"
                    data-testid="zip-field"
                    onChange={onChangeUserZipCode}
                    onBlur={onBlurUserZipCode}
                    value={userZipCode}
                    errorText={userZipCodeErrorMessage}
                    error={userZipCodeErrorMessage !== ''}
                    check={isUserZipCodeValid}
                    disabled={isProcessing}
                    description={zipDescription || zipHint}
                    label={t('ZIP_CODE_LABEL')}
                    full
                  />
                </div>
                <div>
                  <div className="w-full mb-24">
                    <TextField
                      type="text"
                      maxLength={50}
                      id="userAddress"
                      name="userAddress"
                      data-testid="address-field"
                      full
                      onChange={onChangeUserAddress}
                      onBlur={onBlurUserAddress}
                      value={userAddress}
                      errorText={userAddressErrorMessage}
                      error={userAddressErrorMessage !== ''}
                      check={isUserAddressValid}
                      disabled={isProcessing}
                      label={t('USER_ADDRESS_LABEL')}
                    />
                  </div>
                  <div className="flex flex-col w-full flex-start sm:mb-16 sm:flex-row">
                    <TextField
                      type="text"
                      maxLength={15}
                      id="cityName"
                      name="cityName"
                      data-testid="city-field"
                      full
                      onChange={onChangeCityName}
                      onBlur={onBlurCityName}
                      value={cityName}
                      errorText={cityNameErrorMessage}
                      error={cityNameErrorMessage !== ''}
                      check={isCityNameValid}
                      disabled={isProcessing}
                      label={t('CITY_NAME_LABEL')}
                      className="w-full sm:w-1/2 mb-16 sm:mb-0 sm:mr-16"
                    />

                    <div className="mb-16 sm:mb-0 w-full sm:w-1/2">
                      {isEbanxCardArg ? (
                        <React.Fragment>
                          <TextField
                            type="text"
                            maxLength={30}
                            id="CardState"
                            name="CardState"
                            data-testid="state-field"
                            full
                            onChange={onChangeProvinceOrStateName}
                            // @ts-ignore
                            value={provinceOrStateName}
                            onBlur={onBlurProvinceOrStateName}
                            errorText={provinceOrStateNameErrorMessage}
                            error={provinceOrStateNameErrorMessage !== ''}
                            check={isProvinceOrStateNameValid}
                            disabled={isProcessing}
                            label={t('STATE_NAME_LABEL')}
                          />
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <Dropdown
                            // @ts-ignore
                            options={brazilState}
                            // @ts-ignore
                            selected={provinceOrStateName}
                            // @ts-ignore
                            onSelected={onChangeProvinceOrStateName}
                            required
                            disabled={false}
                            full
                            size={4}
                            data-testid="state"
                            label={t('STATE_NAME_LABEL')}
                          />
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-16">
                  <TextField
                    type="tel"
                    maxLength={phoneMaxLength}
                    id="phoneNumber"
                    name="phoneNumber"
                    data-testid="phone-number-field"
                    full
                    onChange={onChangePhoneNumber}
                    onBlur={onBlurPhoneNumber}
                    value={phoneNumber}
                    errorText={phoneNumberErrorMessage}
                    error={!!phoneNumberErrorMessage}
                    check={isPhoneNumberValid}
                    disabled={isProcessing}
                    startAdornment={
                      <span className="font-body4">{countryCod}</span>
                    }
                    label={t('PHONE_NUMBER_LABEL')}
                  />
                </div>
              </React.Fragment>
            )}
            <div className="w-full sm:max-w-280">
              <PrimaryButton
                isAccent
                full
                data-testid="continue-button"
                disabled={!areFirstStepsFieldsValid}
                label={t('NEXT_FORM')}
                type="submit"
                size="xl"
              />
            </div>
          </div>
        )}
        {/* Card information form fields */}
        {!hideCardSection && (
          <div>
            <div className="w-full mb-24" style={{ maxWidth: 360 }}>
              <AnimatedCard
                // @ts-ignore
                creditCardExpiry={creditCardExpiry}
                // @ts-ignore
                creditCardIssuer={creditCardIssuer}
                // @ts-ignore
                creditCardNumber={creditCardNumber}
                // @ts-ignore
                creditCardCVV={creditCardCVV}
                isCreditCardNumberValid={isCreditCardNumberValid}
                isCreditCardExpiryValid={isCreditCardExpiryValid}
              />
            </div>
            {isEbanxCard && (
              <button
                className="mb-16 block"
                type="button"
                data-testid="go-to-back"
                onClick={() => {
                  setHideCardSection(true);
                }}
              >
                <Crehanaicons175
                  size={12}
                  ignoreColor
                  className="fill-current text-secondary-main mr-8"
                />
                <span className="font-link text-secondary-main">
                  {t('GO_BACK')}
                </span>
              </button>
            )}

            <div className="block">
              <div className="w-full mb-24">
                <TextField
                  autoComplete="cc-number"
                  type="tel"
                  maxLength={creditCardDigitsWithBlankSpaces}
                  id="creditCardNumber"
                  name="creditCardNumber"
                  data-testid="card-number-field"
                  full
                  onChange={onChangeCreditCardNumber}
                  value={creditCardNumber}
                  errorText={creditCardNumberErrorMessage}
                  error={creditCardNumberErrorMessage !== ''}
                  check={isCreditCardNumberValid}
                  disabled={isProcessing}
                  onBlur={onBlurCreditCardNumber}
                  startAdornment={
                    <CardIssuerLogo cardType={creditCardIssuer} />
                  }
                  autoFocus={creditCardNumberFocus}
                  label={
                    <span className="flex items-center justify-between w-full font-subtitle3">
                      <span>{t('CARD_NUMBER_LABEL')}</span>
                      <CardLogos />
                    </span>
                  }
                />
              </div>
              <div className="flex w-full flex-start mb-24">
                <TextField
                  autoComplete="cc-exp"
                  id="creditCardExpiry"
                  className="w-7/12 mr-8 sm:1/12 sm:mr-16"
                  name="creditCardExpiry"
                  data-testid="card-date-field"
                  full
                  errorText={creditCardExpiryErrorMessage}
                  onChange={onChageCreditCardExpiry}
                  maxLength={expiryCardMaxLength}
                  error={creditCardExpiryErrorMessage !== ''}
                  value={creditCardExpiry}
                  check={isCreditCardExpiryValid}
                  disabled={isProcessing}
                  onBlur={onBlurCreditCardExpiry}
                  type="tel"
                  autoFocus={creditCardExpiryFocus}
                  label={t('EXPIRATION_DATE_LABEL')}
                />
                <TextField
                  autoComplete="cc-csc"
                  type="tel"
                  id="creditCardCVV"
                  name="creditCardCVV"
                  data-testid="card-cvv-field"
                  className="w-5/12 sm:1/2"
                  full
                  errorText={creditCardCVVErrorMessage}
                  onChange={onChangeCreditCardCVV}
                  maxLength={creditCardCVVDigits}
                  value={creditCardCVV}
                  error={creditCardCVVErrorMessage !== ''}
                  check={isCreditCardCVVValid}
                  disabled={isProcessing}
                  onBlur={onBlurCVV}
                  autoFocus={creditCardCVVFocus}
                  label={t('CVC_CVV_LABEL')}
                />
              </div>
            </div>
            {isGuestCheckout && (
              <div className="w-full mb-24" style={{ maxWidth: 506 }}>
                {/* @ts-ignore */}
                <InputGuestUser
                  // @ts-ignore
                  onEmailChange={onEmailChange}
                  guestEmail={guestEmail}
                />
              </div>
            )}
            {isColombianCheckout && (
              <div>
                <p className="font-subtitle3 mb-24">{t('CO_DOCUMENT_LABEL')}</p>
                <div className="w-full mb-24">
                  <Radio
                    className="mr-32"
                    checked={documentType === DocumentTypeEnum.CC}
                    id="optionCC"
                    label="CC"
                    name="radio-doc"
                    onChange={onChangeDocumentType}
                    value="CC"
                    data-testid="document-cc"
                  />
                  <Radio
                    className="mr-32"
                    checked={documentType === DocumentTypeEnum.CE}
                    id="optionCE"
                    label="CE"
                    name="radio-doc"
                    onChange={onChangeDocumentType}
                    value="CE"
                    data-testid="document-ce"
                  />
                </div>
                <TextField
                  label="Número de documento"
                  className="w-full mb-24"
                  onChange={onChangeIdentityDocument}
                  maxLength={documentType === 'CC' ? 10 : 6}
                  value={identityDocument}
                  check={isIdentityDocumentValid}
                  onBlur={onBlurIdentityDocument}
                  error={!!identityDocumentErrorMessage}
                  errorText={identityDocumentErrorMessage}
                  data-testid="document-field"
                />
              </div>
            )}
            {isEbanxCard && (
              <div className="w-full mb-24">
                <TextField
                  type="text"
                  maxLength={40}
                  id="fullName"
                  name="fullName"
                  data-testid="fullname-field"
                  full
                  onChange={onChangeFullName}
                  value={fullName}
                  errorText={fullNameErrorMessage}
                  error={fullNameErrorMessage !== ''}
                  check={isFullNameValid}
                  disabled={isProcessing}
                  onBlur={onBlurFullName}
                  label={t('CARD_HOLDER_LABEL')}
                />
              </div>
            )}
          </div>
        )}
        {paymentError && (
          <p className="mt-8 mb-8 font-legals text-red-main" role="alert">
            {paymentError}
          </p>
        )}

        <QuotasSelector
          symbol={symbol}
          quotas={quotas}
          selectedQuotaNumber={selectedQuotaNumber}
          setSelectedQuotaNumber={setSelectedQuotaNumber}
        />

        {!hideCardSection && (
          <div className="mx-auto sm:mx-0 sm:mt-0">
            <LegalCheckbox
              className="mb-24"
              checkedTerms={checkedTerms}
              setCheckedTerms={setCheckedTerms}
              isMembership={isMembership}
            />
            <NewsLetterCheckbox
              className="mb-40"
              checkedNewsLetterTerms={checkedNewsLetterTerms}
              setCheckedNewsLetterTerms={setCheckedNewsLetterTerms}
            />
            {showPyamentButton && (
              <div className="w-full sm:max-w-360">
                <PrimaryButton
                  isAccent
                  disabled={!isPaymentButtonActive || isProcessing}
                  data-testid="buy-product-submit"
                  full
                  size="xl"
                  label={t('BUY_NOW')}
                  type="submit"
                />
              </div>
            )}
          </div>
        )}
      </form>
      {showPyamentButton && (
        <SecurePayment className="mt-32" isVariant={false} />
      )}
      {isProcessing && (
        <PaymentsLoader type="purchaseOnline" cardType={creditCardIssuer} />
      )}
    </div>
  );
};

export default Form;
